import { useEffect, useState } from 'react';
import { sequencesAPI, emailTemplatesAPI, leadsAPI, scheduledEmailsAPI } from '../api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { 
  Plus, Search, Mail, Play, Pause, Users, Send, Clock, 
  CheckCircle, XCircle, Edit, Trash2, ArrowRight, Zap
} from 'lucide-react';
import { format } from 'date-fns';

export const SequencesPage = () => {
  const [sequences, setSequences] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSequence, setSelectedSequence] = useState(null);
  const [sequenceStats, setSequenceStats] = useState(null);
  const [enrollments, setEnrollments] = useState([]);
  const [scheduledEmails, setScheduledEmails] = useState([]);
  
  // Dialog states
  const [isSequenceDialogOpen, setIsSequenceDialogOpen] = useState(false);
  const [isTemplateDialogOpen, setIsTemplateDialogOpen] = useState(false);
  const [isEnrollDialogOpen, setIsEnrollDialogOpen] = useState(false);
  const [editingSequence, setEditingSequence] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState(null);

  const [sequenceForm, setSequenceForm] = useState({
    name: '',
    description: '',
    trigger: 'manual',
    trigger_condition: {},
    steps: []
  });

  const [templateForm, setTemplateForm] = useState({
    name: '',
    subject: '',
    body: '',
    variables: []
  });

  const [enrollForm, setEnrollForm] = useState({
    lead_id: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [seqRes, tplRes, leadsRes] = await Promise.all([
        sequencesAPI.getAll(),
        emailTemplatesAPI.getAll(),
        leadsAPI.getAll()
      ]);
      setSequences(seqRes.data);
      setTemplates(tplRes.data);
      setLeads(leadsRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectSequence = async (sequence) => {
    setSelectedSequence(sequence);
    try {
      const [statsRes, enrollRes, emailsRes] = await Promise.all([
        sequencesAPI.getStats(sequence.id),
        sequencesAPI.getEnrollments(sequence.id),
        scheduledEmailsAPI.getAll({ sequence_id: sequence.id })
      ]);
      setSequenceStats(statsRes.data);
      setEnrollments(enrollRes.data);
      setScheduledEmails(emailsRes.data);
    } catch (error) {
      toast.error('Failed to fetch sequence details');
    }
  };

  const handleCreateSequence = async (e) => {
    e.preventDefault();
    try {
      if (editingSequence) {
        await sequencesAPI.update(editingSequence.id, sequenceForm);
        toast.success('Sequence updated');
      } else {
        await sequencesAPI.create(sequenceForm);
        toast.success('Sequence created');
      }
      setIsSequenceDialogOpen(false);
      resetSequenceForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save sequence');
    }
  };

  const handleCreateTemplate = async (e) => {
    e.preventDefault();
    try {
      if (editingTemplate) {
        await emailTemplatesAPI.update(editingTemplate.id, templateForm);
        toast.success('Template updated');
      } else {
        await emailTemplatesAPI.create(templateForm);
        toast.success('Template created');
      }
      setIsTemplateDialogOpen(false);
      resetTemplateForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save template');
    }
  };

  const handleEnroll = async (e) => {
    e.preventDefault();
    if (!selectedSequence) return;
    try {
      await sequencesAPI.enroll(selectedSequence.id, enrollForm);
      toast.success('Lead enrolled in sequence');
      setIsEnrollDialogOpen(false);
      setEnrollForm({ lead_id: '' });
      handleSelectSequence(selectedSequence);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to enroll');
    }
  };

  const handleToggleSequenceStatus = async (sequence) => {
    const newStatus = sequence.status === 'active' ? 'paused' : 'active';
    try {
      await sequencesAPI.update(sequence.id, { status: newStatus });
      toast.success(`Sequence ${newStatus === 'active' ? 'activated' : 'paused'}`);
      fetchData();
      if (selectedSequence?.id === sequence.id) {
        setSelectedSequence({ ...selectedSequence, status: newStatus });
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDeleteSequence = async (id) => {
    if (!window.confirm('Delete this sequence?')) return;
    try {
      await sequencesAPI.delete(id);
      toast.success('Sequence deleted');
      if (selectedSequence?.id === id) {
        setSelectedSequence(null);
      }
      fetchData();
    } catch (error) {
      toast.error('Failed to delete sequence');
    }
  };

  const handleDeleteTemplate = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    try {
      await emailTemplatesAPI.delete(id);
      toast.success('Template deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  const handleSendEmail = async (emailId) => {
    try {
      await scheduledEmailsAPI.send(emailId);
      toast.success('Email sent (simulated)');
      if (selectedSequence) {
        handleSelectSequence(selectedSequence);
      }
    } catch (error) {
      toast.error('Failed to send email');
    }
  };

  const handleCancelEnrollment = async (enrollmentId) => {
    try {
      await sequencesAPI.cancelEnrollment(enrollmentId);
      toast.success('Enrollment cancelled');
      if (selectedSequence) {
        handleSelectSequence(selectedSequence);
      }
    } catch (error) {
      toast.error('Failed to cancel enrollment');
    }
  };

  const addStep = () => {
    setSequenceForm(prev => ({
      ...prev,
      steps: [...prev.steps, { step_number: prev.steps.length + 1, template_id: '', delay_days: 0, delay_hours: 0 }]
    }));
  };

  const updateStep = (index, field, value) => {
    setSequenceForm(prev => ({
      ...prev,
      steps: prev.steps.map((s, i) => i === index ? { ...s, [field]: value } : s)
    }));
  };

  const removeStep = (index) => {
    setSequenceForm(prev => ({
      ...prev,
      steps: prev.steps.filter((_, i) => i !== index).map((s, i) => ({ ...s, step_number: i + 1 }))
    }));
  };

  const resetSequenceForm = () => {
    setEditingSequence(null);
    setSequenceForm({ name: '', description: '', trigger: 'manual', trigger_condition: {}, steps: [] });
  };

  const resetTemplateForm = () => {
    setEditingTemplate(null);
    setTemplateForm({ name: '', subject: '', body: '', variables: [] });
  };

  const editSequence = (sequence) => {
    setEditingSequence(sequence);
    setSequenceForm({
      name: sequence.name,
      description: sequence.description || '',
      trigger: sequence.trigger,
      trigger_condition: sequence.trigger_condition || {},
      steps: sequence.steps || []
    });
    setIsSequenceDialogOpen(true);
  };

  const editTemplate = (template) => {
    setEditingTemplate(template);
    setTemplateForm({
      name: template.name,
      subject: template.subject,
      body: template.body,
      variables: template.variables || []
    });
    setIsTemplateDialogOpen(true);
  };

  const getTemplateName = (templateId) => {
    const template = templates.find(t => t.id === templateId);
    return template?.name || 'Unknown Template';
  };

  const getLeadName = (leadId) => {
    const lead = leads.find(l => l.id === leadId);
    return lead ? `${lead.first_name} ${lead.last_name}` : 'Unknown';
  };

  const statusColors = {
    draft: 'bg-slate-100 text-slate-600',
    active: 'bg-emerald-100 text-emerald-700',
    paused: 'bg-amber-100 text-amber-700',
    completed: 'bg-blue-100 text-blue-700'
  };

  const emailStatusColors = {
    pending: 'bg-amber-100 text-amber-700',
    sent: 'bg-emerald-100 text-emerald-700',
    failed: 'bg-red-100 text-red-700',
    cancelled: 'bg-slate-100 text-slate-600'
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="sequences-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold font-['Manrope'] heading-tight">Email Sequences</h1>
          <p className="text-muted-foreground mt-1">Automate lead nurturing campaigns</p>
        </div>
        <div className="flex gap-2">
          {/* Template Dialog */}
          <Dialog open={isTemplateDialogOpen} onOpenChange={(o) => { setIsTemplateDialogOpen(o); if (!o) resetTemplateForm(); }}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="add-template-btn">
                <Mail className="h-4 w-4 mr-2" />
                New Template
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>{editingTemplate ? 'Edit Template' : 'Create Email Template'}</DialogTitle>
                <DialogDescription>Design an email template for your sequences</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateTemplate} className="space-y-4">
                <div className="space-y-2">
                  <Label>Template Name *</Label>
                  <Input
                    value={templateForm.name}
                    onChange={(e) => setTemplateForm(p => ({ ...p, name: e.target.value }))}
                    required
                    data-testid="template-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Subject Line *</Label>
                  <Input
                    value={templateForm.subject}
                    onChange={(e) => setTemplateForm(p => ({ ...p, subject: e.target.value }))}
                    placeholder="Use {{first_name}} for personalization"
                    required
                    data-testid="template-subject-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email Body *</Label>
                  <Textarea
                    value={templateForm.body}
                    onChange={(e) => setTemplateForm(p => ({ ...p, body: e.target.value }))}
                    placeholder="Hi {{first_name}},\n\nYour email content here..."
                    rows={8}
                    required
                    data-testid="template-body-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    Available variables: {"{{first_name}}"}, {"{{name}}"}, {"{{company_name}}"}
                  </p>
                </div>
                <DialogFooter>
                  <Button type="submit" data-testid="template-submit-btn">
                    {editingTemplate ? 'Update' : 'Create'} Template
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>

          {/* Sequence Dialog */}
          <Dialog open={isSequenceDialogOpen} onOpenChange={(o) => { setIsSequenceDialogOpen(o); if (!o) resetSequenceForm(); }}>
            <DialogTrigger asChild>
              <Button data-testid="add-sequence-btn">
                <Plus className="h-4 w-4 mr-2" />
                New Sequence
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh]">
              <DialogHeader>
                <DialogTitle>{editingSequence ? 'Edit Sequence' : 'Create Email Sequence'}</DialogTitle>
                <DialogDescription>Set up automated email drip campaign</DialogDescription>
              </DialogHeader>
              <ScrollArea className="max-h-[60vh] pr-4">
                <form onSubmit={handleCreateSequence} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Sequence Name *</Label>
                    <Input
                      value={sequenceForm.name}
                      onChange={(e) => setSequenceForm(p => ({ ...p, name: e.target.value }))}
                      placeholder="e.g., New Lead Welcome Series"
                      required
                      data-testid="sequence-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Textarea
                      value={sequenceForm.description}
                      onChange={(e) => setSequenceForm(p => ({ ...p, description: e.target.value }))}
                      rows={2}
                      data-testid="sequence-description-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Trigger</Label>
                    <Select value={sequenceForm.trigger} onValueChange={(v) => setSequenceForm(p => ({ ...p, trigger: v }))}>
                      <SelectTrigger data-testid="sequence-trigger-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="manual">Manual Enrollment</SelectItem>
                        <SelectItem value="lead_created">New Lead Created</SelectItem>
                        <SelectItem value="lead_status_change">Lead Status Change</SelectItem>
                        <SelectItem value="deal_stage_change">Deal Stage Change</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Steps */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label>Sequence Steps</Label>
                      <Button type="button" variant="outline" size="sm" onClick={addStep} data-testid="add-step-btn">
                        <Plus className="h-3 w-3 mr-1" /> Add Step
                      </Button>
                    </div>
                    
                    {sequenceForm.steps.length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-4 border rounded-md">
                        No steps yet. Add a step to start building your sequence.
                      </p>
                    ) : (
                      <div className="space-y-3">
                        {sequenceForm.steps.map((step, index) => (
                          <Card key={index} className="p-4">
                            <div className="flex items-start gap-4">
                              <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary text-primary-foreground text-sm font-medium">
                                {index + 1}
                              </div>
                              <div className="flex-1 space-y-3">
                                <div className="grid grid-cols-2 gap-3">
                                  <div className="space-y-1">
                                    <Label className="text-xs">Email Template</Label>
                                    <Select 
                                      value={step.template_id || "none"} 
                                      onValueChange={(v) => updateStep(index, 'template_id', v === "none" ? "" : v)}
                                    >
                                      <SelectTrigger>
                                        <SelectValue placeholder="Select template" />
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="none">Select template</SelectItem>
                                        {templates.map(t => (
                                          <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  </div>
                                  <div className="grid grid-cols-2 gap-2">
                                    <div className="space-y-1">
                                      <Label className="text-xs">Delay (days)</Label>
                                      <Input
                                        type="number"
                                        min="0"
                                        value={step.delay_days}
                                        onChange={(e) => updateStep(index, 'delay_days', parseInt(e.target.value) || 0)}
                                      />
                                    </div>
                                    <div className="space-y-1">
                                      <Label className="text-xs">Hours</Label>
                                      <Input
                                        type="number"
                                        min="0"
                                        max="23"
                                        value={step.delay_hours}
                                        onChange={(e) => updateStep(index, 'delay_hours', parseInt(e.target.value) || 0)}
                                      />
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <Button type="button" variant="ghost" size="icon" onClick={() => removeStep(index)}>
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </div>
                          </Card>
                        ))}
                      </div>
                    )}
                  </div>

                  <DialogFooter>
                    <Button type="submit" data-testid="sequence-submit-btn">
                      {editingSequence ? 'Update' : 'Create'} Sequence
                    </Button>
                  </DialogFooter>
                </form>
              </ScrollArea>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Sequences List */}
        <div className="lg:col-span-1 space-y-4">
          <Tabs defaultValue="sequences">
            <TabsList className="w-full">
              <TabsTrigger value="sequences" className="flex-1">Sequences</TabsTrigger>
              <TabsTrigger value="templates" className="flex-1">Templates</TabsTrigger>
            </TabsList>
            
            <TabsContent value="sequences" className="mt-4 space-y-3">
              {sequences.length > 0 ? sequences.map(seq => (
                <Card 
                  key={seq.id} 
                  className={`cursor-pointer transition-all ${selectedSequence?.id === seq.id ? 'ring-2 ring-primary' : 'hover:shadow-md'}`}
                  onClick={() => handleSelectSequence(seq)}
                  data-testid={`sequence-card-${seq.id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-medium">{seq.name}</h3>
                        <p className="text-xs text-muted-foreground mt-1">
                          {seq.steps?.length || 0} steps • {seq.enrolled_count || 0} enrolled
                        </p>
                      </div>
                      <Badge className={statusColors[seq.status]}>{seq.status}</Badge>
                    </div>
                    <div className="flex gap-2 mt-3">
                      <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); handleToggleSequenceStatus(seq); }}>
                        {seq.status === 'active' ? <Pause className="h-3 w-3" /> : <Play className="h-3 w-3" />}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); editSequence(seq); }}>
                        <Edit className="h-3 w-3" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); handleDeleteSequence(seq.id); }}>
                        <Trash2 className="h-3 w-3 text-destructive" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )) : (
                <Card>
                  <CardContent className="py-8 text-center text-muted-foreground">
                    <Zap className="h-10 w-10 mx-auto mb-2 text-slate-300" />
                    No sequences yet
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            <TabsContent value="templates" className="mt-4 space-y-3">
              {templates.length > 0 ? templates.map(tpl => (
                <Card key={tpl.id} className="hover:shadow-md transition-all">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-medium">{tpl.name}</h3>
                        <p className="text-xs text-muted-foreground mt-1 line-clamp-1">{tpl.subject}</p>
                      </div>
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" onClick={() => editTemplate(tpl)}>
                          <Edit className="h-3 w-3" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleDeleteTemplate(tpl.id)}>
                          <Trash2 className="h-3 w-3 text-destructive" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )) : (
                <Card>
                  <CardContent className="py-8 text-center text-muted-foreground">
                    <Mail className="h-10 w-10 mx-auto mb-2 text-slate-300" />
                    No templates yet
                  </CardContent>
                </Card>
              )}
            </TabsContent>
          </Tabs>
        </div>

        {/* Sequence Details */}
        <div className="lg:col-span-2">
          {selectedSequence ? (
            <div className="space-y-6">
              {/* Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-3">
                      <Users className="h-8 w-8 text-blue-600" />
                      <div>
                        <p className="text-2xl font-bold">{sequenceStats?.total_enrolled || 0}</p>
                        <p className="text-xs text-muted-foreground">Total Enrolled</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-3">
                      <Play className="h-8 w-8 text-emerald-600" />
                      <div>
                        <p className="text-2xl font-bold">{sequenceStats?.active_enrollments || 0}</p>
                        <p className="text-xs text-muted-foreground">Active</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-3">
                      <Send className="h-8 w-8 text-purple-600" />
                      <div>
                        <p className="text-2xl font-bold">{sequenceStats?.emails_sent || 0}</p>
                        <p className="text-xs text-muted-foreground">Emails Sent</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-3">
                      <CheckCircle className="h-8 w-8 text-slate-600" />
                      <div>
                        <p className="text-2xl font-bold">{sequenceStats?.completed || 0}</p>
                        <p className="text-xs text-muted-foreground">Completed</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Sequence Flow */}
              <Card>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">{selectedSequence.name}</CardTitle>
                    <Dialog open={isEnrollDialogOpen} onOpenChange={setIsEnrollDialogOpen}>
                      <DialogTrigger asChild>
                        <Button size="sm" disabled={selectedSequence.status !== 'active'} data-testid="enroll-lead-btn">
                          <Plus className="h-4 w-4 mr-1" /> Enroll Lead
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Enroll Lead in Sequence</DialogTitle>
                        </DialogHeader>
                        <form onSubmit={handleEnroll} className="space-y-4">
                          <div className="space-y-2">
                            <Label>Select Lead</Label>
                            <Select value={enrollForm.lead_id || "none"} onValueChange={(v) => setEnrollForm({ lead_id: v === "none" ? "" : v })}>
                              <SelectTrigger data-testid="enroll-lead-select">
                                <SelectValue placeholder="Choose a lead" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">Select a lead</SelectItem>
                                {leads.filter(l => l.status !== 'converted').map(lead => (
                                  <SelectItem key={lead.id} value={lead.id}>
                                    {lead.first_name} {lead.last_name} - {lead.email}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <DialogFooter>
                            <Button type="submit" disabled={!enrollForm.lead_id} data-testid="enroll-submit-btn">
                              Enroll
                            </Button>
                          </DialogFooter>
                        </form>
                      </DialogContent>
                    </Dialog>
                  </div>
                  <CardDescription>{selectedSequence.description || 'No description'}</CardDescription>
                </CardHeader>
                <CardContent>
                  {/* Steps visualization */}
                  <div className="flex items-center gap-2 overflow-x-auto py-4">
                    {selectedSequence.steps?.map((step, i) => (
                      <div key={i} className="flex items-center">
                        <div className="flex flex-col items-center min-w-[120px]">
                          <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-medium">
                            {i + 1}
                          </div>
                          <p className="text-xs mt-2 text-center">{getTemplateName(step.template_id)}</p>
                          <p className="text-xs text-muted-foreground">
                            {step.delay_days > 0 && `${step.delay_days}d`} {step.delay_hours > 0 && `${step.delay_hours}h`}
                            {step.delay_days === 0 && step.delay_hours === 0 && 'Immediate'}
                          </p>
                        </div>
                        {i < selectedSequence.steps.length - 1 && (
                          <ArrowRight className="h-4 w-4 text-muted-foreground mx-2" />
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Scheduled Emails */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Scheduled Emails</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Recipient</TableHead>
                        <TableHead>Step</TableHead>
                        <TableHead>Scheduled</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {scheduledEmails.length > 0 ? scheduledEmails.slice(0, 10).map(email => (
                        <TableRow key={email.id}>
                          <TableCell>
                            <div>
                              <p className="font-medium text-sm">{email.recipient_name}</p>
                              <p className="text-xs text-muted-foreground">{email.recipient_email}</p>
                            </div>
                          </TableCell>
                          <TableCell>Step {email.step_number}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1 text-sm">
                              <Clock className="h-3 w-3" />
                              {format(new Date(email.scheduled_at), 'MMM d, h:mm a')}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge className={emailStatusColors[email.status]}>{email.status}</Badge>
                          </TableCell>
                          <TableCell>
                            {email.status === 'pending' && (
                              <Button size="sm" variant="ghost" onClick={() => handleSendEmail(email.id)}>
                                <Send className="h-3 w-3 mr-1" /> Send
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      )) : (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center py-6 text-muted-foreground">
                            No scheduled emails
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>

              {/* Enrollments */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Enrollments</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Lead</TableHead>
                        <TableHead>Current Step</TableHead>
                        <TableHead>Enrolled</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {enrollments.length > 0 ? enrollments.map(enrollment => (
                        <TableRow key={enrollment.id}>
                          <TableCell className="font-medium">{getLeadName(enrollment.lead_id)}</TableCell>
                          <TableCell>Step {enrollment.current_step + 1}</TableCell>
                          <TableCell>{format(new Date(enrollment.enrolled_at), 'MMM d, yyyy')}</TableCell>
                          <TableCell>
                            <Badge className={statusColors[enrollment.status]}>{enrollment.status}</Badge>
                          </TableCell>
                          <TableCell>
                            {enrollment.status === 'active' && (
                              <Button size="sm" variant="ghost" onClick={() => handleCancelEnrollment(enrollment.id)}>
                                <XCircle className="h-3 w-3 mr-1" /> Cancel
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      )) : (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center py-6 text-muted-foreground">
                            No enrollments yet
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </div>
          ) : (
            <Card className="h-full flex items-center justify-center">
              <CardContent className="text-center py-12">
                <Zap className="h-16 w-16 mx-auto mb-4 text-slate-200" />
                <h3 className="text-lg font-medium">Select a sequence</h3>
                <p className="text-muted-foreground mt-1">Choose a sequence to view details and manage enrollments</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default SequencesPage;
